import { defineStore } from 'pinia'
import { ref } from 'vue'
import { loadSettings, saveSettings } from '@/db/repositories'
import { DEFAULT_SETTINGS, DEFAULT_AI_SETTINGS, type Settings, type Theme, type AppLocale, type AiSettings } from '@/types/settings'
import { setLocale } from '@/i18n'

/**
 * Store global de preferencias.
 *
 * Carga los ajustes desde IndexedDB al arrancar y los persiste
 * automáticamente al cambiar (persistencia reactiva). Los temas
 * del lector (claro/oscuro/sepia) se aplican vía variables CSS
 * que este store actualiza en el documento.
 */
export const useSettingsStore = defineStore('settings', () => {
  const settings = ref<Settings>(structuredClone(DEFAULT_SETTINGS))
  const loaded = ref(false)

  /**
   * Timestamp ISO de la última versión de la config IA que llegó desde la
   * nube (o que subimos). Sirve para (a) comparar nube vs local y (b)
   * evitar que el watcher de push dispare cuando el cambio viene de un
   * pull remoto (`applyingRemoteAi`).
   */
  const aiRemoteVersion = ref<string | null>(null)
  /** Flag que bloquea el push mientras aplicamos un pull remoto. */
  let applyingRemoteAi = false

  /**
   * Estado de la última operación de sync de la config IA, para que la UI
   * muestre un badge honesto (no basta con "la clave está desbloqueada").
   *   - 'idle': nunca se ha intentado sincronizar en esta sesión.
   *   - 'synced': el último pull/push aplicó/subió datos correctamente.
   *   - 'error': el último pull falló (descifrado, red, remoto descartado).
   *   - 'local': la clave de cifrado no está desbloqueada (solo local).
   */
  const aiSyncStatus = ref<'idle' | 'synced' | 'error' | 'local'>('idle')

  /** Actualiza el estado de sync de la config IA (lo llama el composable de sync). */
  function setAiSyncStatus(status: 'idle' | 'synced' | 'error' | 'local') {
    aiSyncStatus.value = status
  }

  /** Carga inicial desde IndexedDB. Llamar en `main.ts`. */
  async function init() {
    settings.value = await loadSettings()
    // Inicializar la marca de versión de la nube con el `lastSyncedAt`
    // persistido, para que el primer pull sepa si la nube es realmente más
    // nueva (y no pise lo local sin razón al reabrir la app).
    aiRemoteVersion.value = settings.value.ai.lastSyncedAt ?? null
    // Sync es automático: si hay credenciales (env), `enabled` debe ser
    // true sin importar lo persistido (usuarios viejos podían tener false).
    // Si no hay credenciales, forzamos false para que todo opere local.
    const hasCreds =
      Boolean(settings.value.sync.supabaseUrl) &&
      Boolean(settings.value.sync.supabaseAnonKey)
    if (settings.value.sync.enabled !== hasCreds) {
      settings.value.sync.enabled = hasCreds
      await persist()
    }
    loaded.value = true
    applyAppMode()
  }

  /** Persiste el estado actual en IndexedDB. */
  async function persist() {
    // No usar structuredClone: settings.value es un Proxy reactivo de
    // Vue y structuredClone lanza DataCloneError. JSON copy es seguro
    // aquí porque Settings no contiene Blobs.
    await saveSettings(JSON.parse(JSON.stringify(settings.value)))
  }

  /** Reemplaza los ajustes completos y persiste. */
  async function update(patch: DeepPartial<Settings>) {
    const merged = mergeDeep(
      // Copia profunda de DEFAULTS (que es un objeto plano, no reactivo).
      JSON.parse(JSON.stringify(DEFAULT_SETTINGS)) as Record<string, unknown>,
      JSON.parse(JSON.stringify(settings.value)) as Record<string, unknown>,
      patch as Record<string, unknown>,
    ) as unknown as Settings
    // Si el patch toca la config IA, marcar lo local como "sucio" para que
    // el pull de la nube no lo pise: el cambio aún no se ha sincronizado
    // (lo hará el push debounced). Sin esto, al reabrir la app el pull veía
    // la nube como "más nueva" (porque su updated_at > lastSyncedAt stale)
    // y revertía ediciones locales como el modelo elegido.
    // applyRemoteAi y updateAiLastSyncedAt NO usan update(), así que esto
    // no interfiere con el flujo de sync.
    if (patch.ai) {
      merged.ai = { ...merged.ai, lastSyncedAt: null }
    }
    settings.value = merged
    await persist()
    applyAppMode()
  }

  function setTheme(theme: Theme) {
    return update({ theme })
  }

  /** Cambia el idioma de la interfaz y lo aplica en caliente. */
  function changeLocale(locale: AppLocale) {
    void update({ locale })
    setLocale(locale)
  }

  /** Modo claro/oscuro/sistema de la SHELL (no del contenido del lector). */
  function applyAppMode() {
    const mode = settings.value.appMode
    const root = document.documentElement
    const wantsDark =
      mode === 'dark' ||
      (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    root.classList.toggle('dark', wantsDark)
  }

  /**
   * Registra el `updated_at` de la versión de la config IA actualmente
   * sincronizada con la nube. Lo llama el composable de sync tras un
   * pull o push exitosos.
   */
  function markAiSynced(isoTimestamp: string) {
    aiRemoteVersion.value = isoTimestamp
  }

  /**
   * Persiste `lastSyncedAt` dentro de `ai` (en IndexedDB) y actualiza la
   * marca en memoria. Lo llama el sync tras un push exitoso para que, al
   * reabrir la app, el pull sepa que lo local ya está al día con la nube.
   */
  async function updateAiLastSyncedAt(isoTimestamp: string) {
    settings.value = {
      ...settings.value,
      ai: { ...settings.value.ai, lastSyncedAt: isoTimestamp },
    }
    await persist()
  }

  /**
   * Aplica una config IA recibida de la nube (nube gana) SIN disparar el
   * watcher de push (evita un bucle pull→push inmediato). Persiste local.
   */
  async function applyRemoteAi(ai: AiSettings) {
    applyingRemoteAi = true
    try {
      settings.value = { ...settings.value, ai }
      await persist()
    } finally {
      applyingRemoteAi = false
    }
  }

  /** ¿Estamos en medio de aplicar un pull remoto? (para que el watcher no pushee). */
  function isApplyingRemoteAi() {
    return applyingRemoteAi
  }

  /**
   * Borra la config IA local (API keys, endpoint/token del worker) y la
   * persiste así en IndexedDB. Lo llama `auth.signOut()` para que en un
   * dispositivo compartido no queden claves en claro tras cerrar sesión.
   * La copia cifrada de la nube (ligada a la cuenta) se conserva; al volver
   * a iniciar sesión, el pull la recarga automáticamente.
   */
  async function clearLocalAi() {
    settings.value = {
      ...settings.value,
      ai: structuredClone(DEFAULT_AI_SETTINGS),
    }
    aiRemoteVersion.value = null
    aiSyncStatus.value = 'idle'
    await persist()
  }

  return {
    settings,
    loaded,
    aiRemoteVersion,
    aiSyncStatus,
    setAiSyncStatus,
    init,
    persist,
    update,
    setTheme,
    changeLocale,
    applyAppMode,
    markAiSynced,
    updateAiLastSyncedAt,
    applyRemoteAi,
    isApplyingRemoteAi,
    clearLocalAi,
  }
})

// ---------- helpers de merge ----------
//
// `mergeDeep` produce un objeto plano resultado de fusionar `sources`
// de izquierda a derecha (los posteriores ganan). La fusion de la
// primera fuente (DEFAULT_SETTINGS) garantiza que todo campo existira,
// por lo que el cast final a `Settings` es seguro.

type AnyRecord = Record<string, unknown>

/**
 * Hace todos los campos opcionales recursivamente.
 * Usamos `object` como predicado (en vez de `AnyRecord`) para que las
 * interfaces de dominio —que no extienden Record— se profundicen.
 */
export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

function isObject(v: unknown): v is AnyRecord {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function mergeDeep(...sources: AnyRecord[]): AnyRecord {
  const out: AnyRecord = {}
  for (const src of sources) {
    if (!src) continue
    for (const [k, v] of Object.entries(src)) {
      if (isObject(v) && isObject(out[k])) {
        out[k] = mergeDeep(out[k], v)
      } else if (v !== undefined) {
        out[k] = v
      }
    }
  }
  return out
}
